<template>
  <v-container>
    <v-sheet height="32px">
      <v-toolbar flat color="white" height="30px">
        <v-toolbar-title small>{{ titleDate }}{{ $t('title.add-menu') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          icon
          dark
          @click.stop="deleteDialog = true"
        >
          <v-icon color="red darken-3">
            mdi-trash-can-outline
          </v-icon>
        </v-btn>
      </v-toolbar>
    </v-sheet>
    
    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
    >
      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model="name"
            :counter="50"
            :rules="nameRules"
            :label="$t('label.menu_name')"
            append-icon="history"
            @click:append="selectHistory"
            required
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-select
            v-model="nutrients"
            :multiple="true"
            item-text="label"
            item-value="id"
            :label="$t('label.nutrients')"
            :rules="nutrientsRules"
            :items="this.getNutrients()"
            chips
            deletable-chips
            return-object
            required
            class="max-height: unset !important;"
          ></v-select>
        </v-col>
      </v-row>
      <v-row justify="space-around" align="center">
        <v-col cols="12">
          <v-menu
            ref="menuStart"
            v-model="startMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="start"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="start"
                :label="$t('label.start')"
                prepend-icon="access_time"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="timeRules"
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="startMenu"
              v-model="start"
              full-width
              format="24hr"
              :allowed-minutes="allowedStep"
              @change="changeStart()"
              @click:hour="changeStart()"
              @click:minute="$refs.menuStart.save(start)"
            ></v-time-picker>
          </v-menu>
        </v-col>
      </v-row>
      <v-row justify="space-around" align="center">
        <v-col cols="12">
          <v-menu
            ref="menuEnd"
            v-model="endMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="end"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
            :rules="timeRules"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="end"
                :label="$t('label.end')"
                prepend-icon="access_time"
                readonly
                v-bind="attrs"
                v-on="on"
                :rules="timeRules"
              ></v-text-field>
            </template>
            <v-time-picker
              v-if="endMenu"
              v-model="end"
              full-width
              format="24hr"
              :allowed-minutes="allowedStep"
              @click:minute="$refs.menuEnd.save(end)"
              :min="start"
            ></v-time-picker>
          </v-menu>
        </v-col>
      </v-row>
      <v-row justify="space-around" align="center">
        <v-col cols="5">
          <v-radio-group
            v-model="unit"
            col
            @change="changeUnit()"
            :label="$t('label.amount')"
          >
            <v-radio
              v-for="(item, index) in this.getUnits()"
              :key="index"
              :label="item.label"
              :value="item.id"
              ></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="7">
          <v-text-field
            v-model="amount"
            type="number"
            :counter="50"
            :suffix="unitLabel"
            required
            :min="1"
            :max="9999"
          >
          </v-text-field>
        </v-col>
      </v-row>
      <v-row justify="space-around" align="center">
        <v-col cols="12">
          <v-checkbox
            v-model="hasAllergy"
            :label="$t('label.allergy')"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-textarea
            :label="$t('label.memo')"
            v-model="memo"
            outlined
          ></v-textarea>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-btn
            block
            :disabled="!valid"
            color="success"
            class="mr-4"
            @click="validate"
          >
            {{ $t('label.save') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-btn
            block
            outlined
            class="mr-4"
            @click="cancel"
          >
            {{ $t('label.cancel') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-dialog
      v-model="deleteDialog"
      max-width="290"
    >
      <v-card>
        <v-card-title class="headline">{{ $t('title.delete-menu') }}</v-card-title>
        <v-card-text>
          {{ $t('msg.delete-confirm') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="gray"
            text
            @click="deleteDialog = false"
          >
            {{ $t('label.cancel') }}
          </v-btn>
          <v-btn
            color="red darken-3"
            text
            @click="deleteMenu()"
          >
            {{ $t('label.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="historyDialog"
    >
      <v-card>
        <v-card-title class="headline">{{ $t('title.history-search') }}</v-card-title>
        <v-card-text>
          <v-autocomplete
            :items="names"
            item-text="name"
            :label="$t('label.menu_name')"
            :no-data-text="$t('msg.no-data-text')"
            :full-height="true"
            hide-no-data
            hide-selected
            return-object
            prepend-icon="search"
            @change="selectedHistory"
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="gray"
            text
            @click="historyDialog = false"
          >
            {{ $t('label.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  props: {
    id: {
      type: String,
      default: null
    }
  },
  data: () => ({
    valid: false,
    deleteDialog: false,
    historyDialog: false,
    nameRules: [
      v => !!v || 'メニュー名は必須入力です。',
      v => (v && v.length <= 50) || 'メニュー名は50文字以内で入力してください。',
    ],
    nutrientsRules: [
      v => !!v || '必須入力です。',
      v => (v && 0 < v.length) || '必須入力です。'
    ],
    timeRules: [
      v => !!v || '必須入力です。',
      // v => (v && v.match(/^[0-2][0-9]:[0-5][0-9]$/)) || 'フォーマットが正しくありません'
    ],
    hash: null,
    name: '',
    names: [],
    search: null,
    nutrients: null,
    startMenu: false,
    endMenu: false,
    start: null,
    end: null,
    memo: '',
    amount: null,
    unit: null,
    unitLabel: '',
    hasAllergy: false
  }),
  computed: {
    titleDate () {
      return this.getCurrentDate(true)
    }
  },
  mounted() {
    const id = this.$route.params['id']
    if (id === null) {
      this.$router.push({ name: 'Dashboard'})
    }
    const menu = this.$storage.getMenu(this.getCurrentDate(false), id)
    if (menu !== null) {
      this.setMenuData(menu)
    }
    this.hash = id
    this.names = this.$storage.getAllMenu()
  },
  methods: {
    allowedStep: m => m % 10 === 0,
    changeStart () {
      if (this.start !== null && (typeof this.start === 'string') && this.start.match(/^[0-2][0-9]:[0-5][0-9]$/)) {
        const startHour = parseInt(this.start.split(':')[0])
        const endHour = startHour === 23 ? 23 : startHour + 1
        const m = this.start.split(':')[1]

        this.end = ('0' + String(endHour)).slice(-2) + ':' + m
      }
    },
    changeUnit () {
      this.unitLabel = this.$units.getUnit(this.unit)
    },
    validate () {
      const currentDate = this.getCurrentDate(false)
      if (currentDate === null || !this.$refs.form.validate()) {
        return
      }
      const nutrients = []
      this.nutrients.forEach((item) => {
        nutrients.push(item.id)
      })
      const data = {
        hash: this.hash,
        name: this.name,
        start: this.$util.formatDate(currentDate, false) + ' ' + this.start,
        end: this.$util.formatDate(currentDate, false) + ' ' + this.end,
        nutrients: nutrients,
        amount: this.amount,
        unit: this.unit,
        hasAllergy: this.hasAllergy,
        memo: this.memo
      }
      const result = this.$storage.save(currentDate, data)
      if (!result) {
        alert('')
      } else {
        this.$router.push({ name: 'Dashboard' })
      }
    },
    cancel () {
      this.$router.go(-1)
    },
    resetValidation () {
      this.$refs.form.resetValidation()
    },
    getNutrients () {
      return this.$nutrients.getOptions()
    },
    getUnits () {
      return this.$units.getOptions()
    },
    getCurrentDate (doFormat) {
      const intDate = parseInt(this.$storage.getFocusDate())
      if (intDate === null) {
        this.$router.push({ name: 'Dashboard' })
      }
      const date = new Date(intDate)
      if (date === null) {
        this.$router.push({ name: 'Dashboard' })
      }
      return doFormat ? this.$util.formatJpMonthDay(date, false) : date
    },
    setMenuData (menu) {
      let nutrients = []
      this.$nutrients.getOptions().forEach((item) => {
        if (menu.nutrients.includes(item.id)) {
          nutrients.push(item)
        }
      })
      this.name = menu.name
      this.nutrients = nutrients
      this.start = menu.start.split(' ')[1]
      this.end = menu.end.split(' ')[1]
      this.memo = menu.memo
      this.amount = menu.amount
      this.unit = menu.unit
      this.hasAllergy = menu.hasAllergy
      this.unitLabel = this.$units.getUnit(this.unit)
    },
    deleteMenu () {
      this.$storage.deleteMenu(this.getCurrentDate(false), this.hash)
      this.deleteDialog = false
      this.$router.push({ name: 'Dashboard' })
    },
    selectHistory () {
      this.historyDialog = true
    },
    selectedHistory (item) {
      this.name = item.name
      this.nutrients = []
      item.nutrients.forEach((item) => {
        const data = this.$nutrients.getData(item)
        if (data !== null) {
          this.nutrients.push(data)
        }
      })
      this.historyDialog = false
    }
  }
};
</script>
<style>
.v-menu__content.theme--light.v-menu__content--fixed.menuable__content__active.v-autocomplete__content {
  max-height: auto !important;
}
.v-menu__content.theme--light.menuable__content__active:not(.v-autocomplete__content) {
  max-height: unset !important;
}
</style>